import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle2,
  XCircle,
  SkipForward,
  ChevronRight,
  ChevronLeft,
  Shield,
  FileText,
  GitBranch,
  Clock,
  AlertCircle,
  PartyPopper,
} from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  Select,
  StatusBadge,
  Textarea,
} from '@/components/ui'

// ── Types ────────────────────────────────────────────────────────────────────

type StepStatus = 'pending' | 'in_progress' | 'completed' | 'skipped' | 'rejected'
type StepType = 'task' | 'approval' | 'form' | 'notification' | 'condition'

interface StepFieldDef {
  id: string
  label: string
  field_key: string
  field_type: string
  is_required: number
  options: { choices?: { value: string; label: string }[] } | null
}

interface InstanceStep {
  id: string
  template_step_id: string
  step_title: string | null
  step_description: string | null
  step_type: StepType
  sort_order: number
  is_required: number
  status: StepStatus
  note: string | null
  started_at: string | null
  completed_at: string | null
  fields?: StepFieldDef[]
  form_values?: Record<string, string | null>
}

export interface SubmittedField { fieldId: string; fieldKey: string; value: string }

interface ProcessInstance {
  id: string
  title: string
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'rejected'
  template_name: string | null
  due_date: string | null
  steps: InstanceStep[]
}

// ── Constants ────────────────────────────────────────────────────────────────

const STEP_TYPE_ICONS: Record<StepType, React.ComponentType<{ className?: string }>> = {
  task: CheckCircle2,
  approval: Shield,
  form: FileText,
  notification: Clock,
  condition: GitBranch,
}

const STEP_TYPE_LABELS: Record<StepType, string> = {
  task: 'Aufgabe',
  approval: 'Freigabe',
  form: 'Formular',
  notification: 'Benachrichtigung',
  condition: 'Bedingung',
}

// ── Sub-components ───────────────────────────────────────────────────────────

function ProgressHeader({
  current,
  total,
  title,
}: {
  current: number
  total: number
  title: string
}) {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0
  return (
    <div className="bg-white border-b border-neutral-200 px-6 py-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-neutral-600">
            {current < total ? (
              <>Schritt <span className="text-neutral-900 font-semibold">{current + 1}</span> von{' '}
              <span className="text-neutral-900 font-semibold">{total}</span></>
            ) : (
              <span className="text-green-600 font-semibold">Alle Schritte abgeschlossen</span>
            )}
          </div>
          <div className="text-sm text-neutral-500 font-medium">{pct}%</div>
        </div>
        <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-500 rounded-full transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-1.5 text-xs text-neutral-500 truncate">{title}</div>
      </div>
    </div>
  )
}

function StepBreadcrumbs({ steps, currentIdx }: { steps: InstanceStep[]; currentIdx: number }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {steps.map((step, idx) => {
        const isActive = idx === currentIdx
        const isDone = step.status === 'completed' || step.status === 'skipped'
        const isRejected = step.status === 'rejected'
        return (
          <div key={step.id} className="flex items-center gap-1">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                isDone
                  ? 'bg-green-100 text-green-700 border border-green-200'
                  : isRejected
                  ? 'bg-red-100 text-red-700 border border-red-200'
                  : isActive
                  ? 'bg-brand-500 text-white'
                  : 'bg-neutral-100 text-neutral-400 border border-neutral-200'
              }`}
            >
              {isDone ? <CheckCircle2 className="w-3.5 h-3.5" /> : isRejected ? <XCircle className="w-3.5 h-3.5" /> : idx + 1}
            </div>
            {idx < steps.length - 1 && (
              <ChevronRight className="w-3 h-3 text-neutral-300 shrink-0" />
            )}
          </div>
        )
      })}
    </div>
  )
}

function StepCard({
  step,
  stepNumber,
  totalSteps,
  onComplete,
  onSkip,
  onReject,
  isPending,
}: {
  step: InstanceStep
  stepNumber: number
  totalSteps: number
  onComplete: (note: string, formData: SubmittedField[]) => void
  onSkip: (note: string) => void
  onReject: (note: string) => void
  isPending: boolean
}) {
  const [note, setNote] = useState('')
  const [showRejectConfirm, setShowRejectConfirm] = useState(false)
  const fieldDefs = step.fields ?? []
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {}
    for (const f of fieldDefs) init[f.id] = step.form_values?.[f.id] ?? ''
    return init
  })
  const TypeIcon = STEP_TYPE_ICONS[step.step_type]

  const isApproval = step.step_type === 'approval'

  const missingRequired = fieldDefs.some((f) => f.is_required === 1 && !String(fieldValues[f.id] ?? '').trim())

  function buildFormData(): SubmittedField[] {
    return fieldDefs.map((f) => ({ fieldId: f.id, fieldKey: f.field_key, value: fieldValues[f.id] ?? '' }))
  }

  function setVal(id: string, v: string) {
    setFieldValues((prev) => ({ ...prev, [id]: v }))
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center shrink-0 mt-0.5">
            <TypeIcon className="w-4 h-4 text-brand-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-neutral-400 font-medium">
                Schritt {stepNumber} von {totalSteps}
              </span>
              <span className="text-xs bg-neutral-100 text-neutral-500 rounded px-1.5 py-0.5">
                {STEP_TYPE_LABELS[step.step_type]}
              </span>
              {step.is_required ? (
                <span className="text-xs text-red-600 font-medium">* Pflicht</span>
              ) : (
                <span className="text-xs text-neutral-400">optional</span>
              )}
            </div>
            <h2 className="text-lg font-semibold text-neutral-900 mt-1">
              {step.step_title ?? 'Schritt'}
            </h2>
          </div>
        </div>
      </Card.Header>
      <Card.Body className="space-y-4">
        {step.step_description && (
          <p className="text-sm text-neutral-600 leading-relaxed">{step.step_description}</p>
        )}

        {isApproval && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg text-sm text-amber-700">
            <Shield className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Dieser Schritt erfordert eine explizite Freigabe oder Ablehnung.</span>
          </div>
        )}

        {fieldDefs.length > 0 && (
          <div className="space-y-3 rounded-lg border border-neutral-200 p-3">
            {fieldDefs.map((f) => {
              const label = f.is_required === 1 ? `${f.label} *` : f.label
              const choices = f.options?.choices ?? []
              if (f.field_type === 'textarea') {
                return <Textarea key={f.id} label={label} value={fieldValues[f.id] ?? ''} onChange={(e) => setVal(f.id, e.target.value)} rows={3} />
              }
              if (f.field_type === 'select' || f.field_type === 'multiselect') {
                return (
                  <Select
                    key={f.id}
                    label={label}
                    value={fieldValues[f.id] ?? ''}
                    onValueChange={(v) => setVal(f.id, v)}
                    options={[{ value: '', label: '—' }, ...choices]}
                  />
                )
              }
              if (f.field_type === 'checkbox') {
                return (
                  <Checkbox
                    key={f.id}
                    label={label}
                    checked={fieldValues[f.id] === 'true'}
                    onChange={(e) => setVal(f.id, e.target.checked ? 'true' : 'false')}
                  />
                )
              }
              const inputType = f.field_type === 'number' ? 'number' : f.field_type === 'date' ? 'date' : f.field_type === 'datetime' ? 'datetime-local' : 'text'
              return <Input key={f.id} label={label} type={inputType} value={fieldValues[f.id] ?? ''} onChange={(e) => setVal(f.id, e.target.value)} />
            })}
          </div>
        )}

        <Textarea
          label="Notiz (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optionale Anmerkung zu diesem Schritt…"
          rows={3}
        />
      </Card.Body>
      <Card.Footer>
        <div className="flex items-center gap-2 flex-wrap">
          {isApproval ? (
            <>
              <Button
                variant="primary"
                onClick={() => onComplete(note, buildFormData())}
                disabled={isPending || missingRequired}
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" />
                Freigeben
              </Button>
              <Button
                variant="danger"
                onClick={() => setShowRejectConfirm(true)}
                disabled={isPending}
              >
                <XCircle className="w-4 h-4 mr-1.5" />
                Ablehnen
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              onClick={() => onComplete(note, buildFormData())}
              disabled={isPending || missingRequired}
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              Schritt abschließen
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          {missingRequired && (
            <span className="text-xs text-red-600 self-center">Bitte Pflichtfelder ausfüllen</span>
          )}
          {!step.is_required && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSkip(note)}
              disabled={isPending}
            >
              <SkipForward className="w-3.5 h-3.5 mr-1" />
              Überspringen
            </Button>
          )}
        </div>

        <ConfirmDialog
          open={showRejectConfirm}
          onCancel={() => setShowRejectConfirm(false)}
          title="Schritt ablehnen?"
          message={
            step.is_required
              ? 'Dieser Pflicht-Schritt wird abgelehnt. Der gesamte Prozess wird dadurch als abgelehnt markiert.'
              : 'Dieser Schritt wird als abgelehnt markiert.'
          }
          confirmLabel="Ablehnen"
          variant="danger"
          onConfirm={() => {
            setShowRejectConfirm(false)
            onReject(note)
          }}
        />
      </Card.Footer>
    </Card>
  )
}

function CompletionScreen({
  instance,
  onViewDetail,
}: {
  instance: ProcessInstance
  onViewDetail: () => void
}) {
  const isRejected = instance.status === 'rejected'
  return (
    <div className="max-w-lg mx-auto text-center py-12">
      <div
        className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
          isRejected ? 'bg-red-100' : 'bg-green-100'
        }`}
      >
        {isRejected ? (
          <XCircle className="w-8 h-8 text-red-600" />
        ) : (
          <PartyPopper className="w-8 h-8 text-green-600" />
        )}
      </div>
      <h2 className={`text-2xl font-bold mb-2 ${isRejected ? 'text-red-700' : 'text-green-700'}`}>
        {isRejected ? 'Prozess abgelehnt' : 'Prozess abgeschlossen!'}
      </h2>
      <p className="text-neutral-500 mb-6">
        {isRejected
          ? 'Ein Pflicht-Schritt wurde abgelehnt. Der Prozess ist beendet.'
          : 'Alle Schritte wurden erfolgreich abgeschlossen.'}
      </p>
      <Button variant="primary" onClick={onViewDetail}>
        <FileText className="w-4 h-4 mr-1.5" />
        Prozessdetails anzeigen
      </Button>
    </div>
  )
}

// ── Main component ───────────────────────────────────────────────────────────

export function ProcessWizardPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [currentIdx, setCurrentIdx] = useState<number>(0)
  const initializedRef = useRef(false)

  const { data: instance, isLoading, error } = useQuery({
    queryKey: ['prozesse-instance', id],
    queryFn: () =>
      apiClient
        .get<ProcessInstance>(`/prozesse/instances/${id}`)
        .then((r) => r.data),
    enabled: !!id,
    refetchInterval: false,
  })

  // Set cursor to first active step once on initial data load
  useEffect(() => {
    if (!instance || initializedRef.current) return
    initializedRef.current = true
    const firstActive = instance.steps.findIndex(
      (s) => s.status === 'pending' || s.status === 'in_progress',
    )
    if (firstActive >= 0) setCurrentIdx(firstActive)
  }, [instance])

  const stepMutation = useMutation({
    mutationFn: ({
      stepId,
      status,
      note,
      formData,
    }: {
      stepId: string
      status: 'completed' | 'rejected' | 'skipped'
      note?: string
      formData?: SubmittedField[]
    }) =>
      apiClient
        .patch(`/prozesse/instances/${id}/steps/${stepId}`, {
          status,
          note: note || undefined,
          formData: formData && formData.length > 0 ? formData : undefined,
        })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prozesse-instance', id] })
      // Advance to next pending step
      if (instance) {
        const nextIdx = instance.steps.findIndex(
          (s, i) => i > currentIdx && (s.status === 'pending' || s.status === 'in_progress'),
        )
        if (nextIdx >= 0) setCurrentIdx(nextIdx)
      }
    },
  })

  const cancelMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/prozesse/instances/${id}/cancel`, {}).then((r) => r.data),
    onSuccess: () => {
      navigate('/prozesse')
    },
  })

  if (isLoading) return <LoadingState label="Prozess wird geladen…" />
  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          title="Fehler beim Laden"
          description="Der Prozess konnte nicht geladen werden."
          details={error instanceof Error ? error.message : String(error)}
        />
      </div>
    )
  }
  if (!instance) return null

  const steps = instance.steps
  const completedCount = steps.filter((s) => s.status === 'completed' || s.status === 'skipped').length
  const isFinished =
    instance.status === 'completed' || instance.status === 'rejected' || instance.status === 'cancelled'

  // Active step: ensure currentIdx points to a non-terminal step
  const activeStep = steps[currentIdx]

  return (
    <div className="min-h-screen bg-neutral-50">
      <ProgressHeader
        current={completedCount}
        total={steps.length}
        title={instance.title}
      />

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <PageHeader
          title={instance.title}
          description={instance.template_name ?? undefined}
          breadcrumbs={[
            { label: 'Prozesse', to: '/prozesse' },
            { label: instance.title },
          ]}
          actions={
            !isFinished ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCancelDialogOpen(true)}
                disabled={cancelMutation.isPending}
              >
                <XCircle className="w-4 h-4 mr-1 text-red-500" />
                Abbrechen
              </Button>
            ) : undefined
          }
        />

        {/* Step breadcrumbs */}
        {steps.length > 0 && (
          <div className="bg-white border border-neutral-200 rounded-lg p-3">
            <StepBreadcrumbs steps={steps} currentIdx={currentIdx} />
          </div>
        )}

        {/* Finished state */}
        {isFinished && (
          <CompletionScreen
            instance={instance}
            onViewDetail={() => navigate(`/prozesse/${id}`)}
          />
        )}

        {/* No steps */}
        {!isFinished && steps.length === 0 && (
          <EmptyState
            icon={<GitBranch className="w-8 h-8" />}
            title="Keine Schritte"
            description="Diese Prozessvorlage enthält keine Schritte."
          />
        )}

        {/* Active step */}
        {!isFinished && activeStep && (
          <>
            {/* Previously completed steps (collapsed) */}
            {currentIdx > 0 && (
              <div className="space-y-1">
                {steps.slice(0, currentIdx).map((step, idx) => (
                  <div
                    key={step.id}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border text-sm ${
                      step.status === 'completed' || step.status === 'skipped'
                        ? 'bg-green-50 border-green-100 text-green-700'
                        : step.status === 'rejected'
                        ? 'bg-red-50 border-red-100 text-red-700'
                        : 'bg-neutral-50 border-neutral-200 text-neutral-500'
                    }`}
                  >
                    {step.status === 'completed' ? (
                      <CheckCircle2 className="w-4 h-4 shrink-0" />
                    ) : step.status === 'skipped' ? (
                      <SkipForward className="w-4 h-4 shrink-0" />
                    ) : step.status === 'rejected' ? (
                      <XCircle className="w-4 h-4 shrink-0" />
                    ) : (
                      <Clock className="w-4 h-4 shrink-0" />
                    )}
                    <span>
                      {idx + 1}. {step.step_title ?? 'Schritt'}
                    </span>
                    <StatusBadge status={step.status} className="ml-auto" />
                  </div>
                ))}
              </div>
            )}

            {/* Current active step */}
            <StepCard
              step={activeStep}
              stepNumber={currentIdx + 1}
              totalSteps={steps.length}
              isPending={stepMutation.isPending}
              onComplete={(note, formData) =>
                stepMutation.mutate({ stepId: activeStep.id, status: 'completed', note, formData })
              }
              onSkip={(note) =>
                stepMutation.mutate({ stepId: activeStep.id, status: 'skipped', note })
              }
              onReject={(note) =>
                stepMutation.mutate({ stepId: activeStep.id, status: 'rejected', note })
              }
            />

            {stepMutation.error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {stepMutation.error instanceof Error
                  ? stepMutation.error.message
                  : 'Fehler beim Speichern'}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentIdx(Math.max(0, currentIdx - 1))}
                disabled={currentIdx === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Zurück
              </Button>

              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/prozesse/${id}`)}
                >
                  <FileText className="w-4 h-4 mr-1" />
                  Übersicht
                </Button>
                {currentIdx < steps.length - 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentIdx(currentIdx + 1)}
                  >
                    Nächster Schritt
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {/* All steps done but instance not yet refreshed */}
        {!isFinished && steps.length > 0 && completedCount === steps.length && (
          <div className="text-center py-6">
            <div className="flex items-center gap-2 justify-center text-green-600 font-medium">
              <CheckCircle2 className="w-5 h-5" />
              Alle Schritte abgeschlossen — Prozess wird aktualisiert…
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={cancelDialogOpen}
        onCancel={() => setCancelDialogOpen(false)}
        title="Prozess abbrechen?"
        message="Der Prozess wird als abgebrochen markiert und kann nicht fortgesetzt werden."
        confirmLabel="Ja, abbrechen"
        variant="danger"
        onConfirm={() => {
          setCancelDialogOpen(false)
          cancelMutation.mutate()
        }}
      />
    </div>
  )
}
